import { cn } from '@/shared/lib'

export type DonutSlice = {
  /** Stable identity — the wire value, not the label. */
  key: string
  label: string
  value: number
  /** The tail the caller folded together. Painted neutral, never on the ramp. */
  isResidual?: boolean
}

type DonutProps = {
  data: DonutSlice[]
  /** Names the chart for screen readers, which cannot read a ring. */
  label: string
  /** Sits in the hole. Defaults to the sum of the slices. */
  total?: number
  /**
   * What the total counts, under it — «хитов». Three rings on one card all show
   * the same number, and without a unit that reads as a bug rather than as the
   * same traffic cut three ways.
   */
  unit?: string
  /** The slice the reader is pointing at, wherever they are pointing at it. */
  activeKey?: string
  onActiveKeyChange?: (key: string | undefined) => void
  className?: string
}

/**
 * Five hues in a fixed order, and a neutral for the folded tail.
 *
 * This was one hue at five falling strengths, which is the wrong encoding: a
 * ramp says «more» and «less», and these slices are names — desktop, Germany,
 * Google Ads. Nothing is more than anything else. It also stopped working the
 * moment the separating gaps came out: two neighbouring steps of one green,
 * arc against arc, are one green.
 *
 * `--chart-1..5` now carries a categorical ramp, ordered and measured for this
 * surface — see the block that defines it in `index.css`. Slice colour comes
 * from **position in the legend**, never from the slice's own value: a filter
 * that drops a category must not repaint the ones that survive.
 *
 * Identity is never colour alone — the legend beside the ring names and counts
 * every slice, which is also the relief the light theme's two sub-3:1 slots
 * require.
 */
const SLOTS = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
]

/**
 * The tail, off the ramp entirely. It has to be visible — it is often the
 * largest slice — without reading as a category of its own, and a sixth hue
 * would claim it is one.
 */
const RESIDUAL = { fill: 'var(--color-muted-foreground)', opacity: 0.68 }

export function donutColor(index: number, isResidual = false): { fill: string; opacity: number } {
  if (isResidual) return RESIDUAL
  const fill = SLOTS[index]
  // Past the ramp is the tail's treatment, not a generated sixth hue: the order
  // is what the palette was validated on, and inventing a slot leaves it.
  if (fill === undefined) return RESIDUAL
  return { fill, opacity: 1 }
}

/*
 * The ring is drawn as filled wedges, one path per slice.
 *
 * It used to be one dashed circle per slice — `pathLength="100"` renamed the
 * circumference so a share was its own dash length, and no arc trigonometry
 * appeared anywhere. That was the appeal, and it was paid for at twelve
 * o'clock: a dash is measured along the *centre* line of a 14-unit stroke, the
 * last slice's dash meets the path's own seam there, and the two ends resolve a
 * fraction of a unit apart. Where they overlapped, two translucent strokes
 * composited into a pale wedge — the notch that survived removing the gaps and
 * then the reveal animation, because neither was ever the cause.
 *
 * A wedge has no such phase. Its edges are two radii and two arcs, written down
 * exactly, and the edge where one slice ends is the edge where the next begins.
 * The arithmetic that dashes made implicit is now in `donutSegments`, where it
 * can be tested.
 */
const CENTER = 50
/** Halfway between the 64% inner and 92% outer edge the design asks for. */
const RADIUS = 39
/** The 28 points between those two edges. */
const WIDTH = 14
/** The active slice thickens outward and inward by a point and a half. */
const WIDTH_ACTIVE = 17
/**
 * Half the hairline each wedge is outlined with, in its own fill colour.
 *
 * Two filled shapes sharing an exact edge are antialiased independently, and
 * the background shows through the seam. The outline grows each wedge by this
 * much all round so the seams close; the radii below are pulled in by the same
 * amount so the ring keeps its measured width. Overlap is invisible because
 * every wedge is opaque — which is also why dimming mixes toward the ground
 * instead of lowering alpha.
 */
const SEAM = 0.3

/** The narrowest arc the ring can still show at this stroke width. */
const MIN_SWEEP = 1.4

/** Point on the ring, clockwise from twelve. */
function pointAt(angle: number, radius: number): [number, number] {
  const rad = ((angle - 90) * Math.PI) / 180
  return [CENTER + radius * Math.cos(rad), CENTER + radius * Math.sin(rad)]
}

const fixed = (n: number) => n.toFixed(3)

/** The wedge between two angles, as a closed path. */
function wedgePath(from: number, to: number, inner: number, outer: number): string {
  const sweep = to - from
  // A single slice covering everything has no wedge — its two radii are the
  // same line. Two subpaths and an even-odd fill make the annulus instead.
  if (sweep >= 360) {
    const ring = (r: number) =>
      `M${fixed(CENTER - r)} ${fixed(CENTER)}` +
      `A${fixed(r)} ${fixed(r)} 0 1 1 ${fixed(CENTER + r)} ${fixed(CENTER)}` +
      `A${fixed(r)} ${fixed(r)} 0 1 1 ${fixed(CENTER - r)} ${fixed(CENTER)}Z`
    return `${ring(outer)}${ring(inner)}`
  }

  const large = sweep > 180 ? 1 : 0
  const [ox0, oy0] = pointAt(from, outer)
  const [ox1, oy1] = pointAt(to, outer)
  const [ix1, iy1] = pointAt(to, inner)
  const [ix0, iy0] = pointAt(from, inner)

  return (
    `M${fixed(ox0)} ${fixed(oy0)}` +
    `A${fixed(outer)} ${fixed(outer)} 0 ${String(large)} 1 ${fixed(ox1)} ${fixed(oy1)}` +
    `L${fixed(ix1)} ${fixed(iy1)}` +
    `A${fixed(inner)} ${fixed(inner)} 0 ${String(large)} 0 ${fixed(ix0)} ${fixed(iy0)}Z`
  )
}

/**
 * Opaque equivalent of a colour at a given weight.
 *
 * Lowering alpha would be simpler and is exactly what left a bright seam where
 * shapes overlap. Mixing toward the page ground — not the card, which is itself
 * a translucent veil — lands on the same colour with nothing to composite.
 */
function solid(fill: string, weight: number): string {
  if (weight >= 1) return fill
  return `color-mix(in oklab, ${fill} ${fixed(weight * 100)}%, var(--color-background))`
}

export type DonutSegment = {
  slice: DonutSlice
  index: number
  /** Degrees clockwise from twelve. */
  from: number
  to: number
}

/**
 * Where each slice starts and ends, in degrees.
 *
 * Exported because this is the whole of the chart's arithmetic: a wrong offset
 * still draws something that looks like a ring, and checking the numbers is the
 * only way to know it is the right one.
 */
export function donutSegments(data: DonutSlice[], sum: number): DonutSegment[] {
  // A share that rounds to nothing still has a legend row pointing at it, so it
  // keeps a hairline of arc instead of disappearing.
  const sweeps = data.map((slice) => Math.max((slice.value / sum) * 360, MIN_SWEEP))

  // Those hairlines are borrowed, not created. Handed out for free they push the
  // total past a full turn, the last wedge laps the first, and an opaque sliver
  // of the tail lands on top of slice one at twelve — the same artefact the
  // dashes used to leave there. The largest slice can spare the degree; it is
  // the one whose length nobody reads off the ring anyway.
  const excess = sweeps.reduce((a, b) => a + b, 0) - 360
  const widest = sweeps.indexOf(Math.max(...sweeps))
  if (excess > 0 && widest >= 0) {
    sweeps[widest] = (sweeps[widest] ?? 0) - excess
  }

  // A running offset rather than an index-based one: the slices are ordered and
  // each starts exactly where the last ended, so no seam can drift.
  let start = 0
  return data.map((slice, index) => {
    const from = start
    start = from + (sweeps[index] ?? 0)
    return { slice, index, from, to: start }
  })
}

export function Donut({
  data,
  label,
  total,
  unit,
  activeKey,
  onActiveKeyChange,
  className,
}: DonutProps) {
  const sum = total ?? data.reduce((acc, slice) => acc + slice.value, 0)
  const isEmpty = sum === 0

  const active = data.find((slice) => slice.key === activeKey)

  const segments = donutSegments(data, sum)

  return (
    <div
      className={cn('relative', className)}
      role="img"
      aria-label={label}
      onPointerLeave={() => onActiveKeyChange?.(undefined)}
    >
      {/*
       * Hidden from assistive tech on purpose. Every value in here is written
       * out as text in the legend beside it, and the recommended treatment for
       * a chart that has a text equivalent is to let the text be the
       * equivalent — not to read the shapes out a second time.
       */}
      <svg viewBox="0 0 100 100" className="aspect-square w-full" aria-hidden>
        {/*
         * The ring arrives by fading up whole, not by being drawn.
         *
         * Every reveal that moves — a sweep round the circle, each slice
         * growing where it stands — puts a hard radial edge on a 14-unit stroke
         * for the length of the animation, and a radial edge across a thick
         * ring is a wedge cut out of it. The gaps between slices were removed
         * for looking like exactly that; an animation that reintroduces the
         * same shape for half a second undoes the fix every time the card
         * loads. Opacity has no geometry to leave an edge in.
         */}
        <g className="animate-ring">
          {isEmpty ? (
            <path
              d={wedgePath(0, 360, RADIUS - WIDTH / 2, RADIUS + WIDTH / 2)}
              fillRule="evenodd"
              fill="var(--color-surface-sunken)"
            />
          ) : (
            segments.map(({ slice, index, from, to }) => {
              const { fill, opacity } = donutColor(index, slice.isResidual)
              const isActive = slice.key === activeKey
              const isDimmed = activeKey !== undefined && !isActive
              const width = isActive ? WIDTH_ACTIVE : WIDTH
              const paint = solid(fill, isDimmed ? opacity * 0.35 : opacity)

              return (
                <path
                  key={slice.key}
                  data-slice={slice.key}
                  d={wedgePath(
                    from,
                    to,
                    RADIUS - width / 2 + SEAM,
                    RADIUS + width / 2 - SEAM,
                  )}
                  fillRule="evenodd"
                  fill={paint}
                  // The wedge wears its own colour as a hairline so the seam
                  // with its neighbour closes. Round joins, or the outline
                  // would spike past the corners of a thin wedge.
                  stroke={paint}
                  strokeWidth={SEAM * 2}
                  strokeLinejoin="round"
                  // Only the wedge takes the pointer, so a slice is hovered
                  // where it is drawn and the hole stays inert.
                  className="cursor-default transition-[fill,stroke] duration-(--vd-dur-base) ease-(--vd-ease-out)"
                  onPointerEnter={() => onActiveKeyChange?.(slice.key)}
                />
              )
            })
          )}
        </g>
      </svg>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-h2 leading-heading text-foreground">
          {(active?.value ?? sum).toLocaleString()}
        </span>
        {/*
         * One line that answers two questions. At rest it says what the total
         * counts — three rings on one card all show the same number, and
         * without a unit that reads as a bug rather than as the same traffic
         * cut three ways. Under the pointer it says that slice's share, which
         * is the one number the legend does not carry. Same slot either way, so
         * nothing moves when the cursor crosses an arc.
         */}
        <span className="font-mono text-label tracking-label text-muted-foreground uppercase">
          {active === undefined ? (unit ?? ' ') : `${((active.value / sum) * 100).toFixed(0)}%`}
        </span>
      </div>
    </div>
  )
}
